import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
    AppCheckboxField,
    AppEquipmentTypeSelectField,
    AppInputField,
    AppPromptsField,
    AppSelectField,
} from '@/form/FormUI';
import type { EquipmentAttributeNodeType, NodeFormProps } from '@/nodes';
import { appEquipmentConfigState, equipmentTypesState, equipmentTypeState, useSavedFormValues } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    EquipmentAttributeDataSchema,
    type EquipmentAttributeDataType,
    type EquipmentTypeId,
    isSelectField,
} from '@rock/types';
import { visibleCoreFields } from '@rock/util';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof EquipmentAttributeDataSchema>;

const EquipmentAttributeForm: FC<NodeFormProps<EquipmentAttributeNodeType>> = ({
    spaceId,
    flowId,
    node,
    updateNode,
    cancelUpdate,
}) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, EquipmentAttributeDataType>({
        resolver: zodResolver(EquipmentAttributeDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const appEquipmentConfig = useAsyncAtomValue(appEquipmentConfigState);
    const type = useAsyncAtomValue(equipmentTypeState(`${watchedFields.equipmentTypeId || '-'}` as EquipmentTypeId));
    const scope = type?.scope ?? 'normal';
    const coreFields = visibleCoreFields(appEquipmentConfig?.coreFields ?? [], { scope, typeId: type?.id });
    const typeFields = type?.inputFields ?? [];
    const fieldOptions = [...coreFields, ...typeFields].map((f) => f.dataKey);

    // A select field renders as tap-to-commit option buttons in the app, so there's no continue
    // button for a label to name.
    const selectedField = [...coreFields, ...typeFields].find((f) => f.dataKey === watchedFields.fieldDataKey);
    const isSelect = !!selectedField && isSelectField(selectedField);

    const onSubmit = useCallback(
        (values: EquipmentAttributeDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What are we asking them to do?" />

                    <AppEquipmentTypeSelectField
                        control={form.control}
                        name="equipmentTypeId"
                        label="Equipment Type"
                        placeholder="Select an Equipment Type"
                        description="Select the Equipment Type that owns the field to capture."
                        equipmentTypes={equipmentTypes}
                        allowContextSelection
                    />

                    <AppSelectField
                        control={form.control}
                        name="fieldDataKey"
                        label="Field"
                        description="Which field from the equipment type should be rendered on this screen?"
                        options={fieldOptions}
                        placeholder={fieldOptions.length > 0 ? 'Select a field' : 'Select an equipment type first'}
                    />

                    <AppCheckboxField
                        label="Allow Skip"
                        control={form.control}
                        name="optional"
                        description="If true, we will allow the user to continue without entering a value."
                    />

                    <AppSelectField
                        label="Help Type"
                        control={form.control}
                        name="helpType"
                        description="What kind of Help button to show. (Help Finding Model Number only appears for 'model-number' fields.)"
                        options={['help-me-decide', 'model-number-help', 'none']}
                    />

                    {!isSelect && (
                        <AppInputField
                            label="Button Label"
                            placeholder="Continue"
                            control={form.control}
                            name="buttonLabel"
                            description="The label on the button to continue."
                        />
                    )}
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default EquipmentAttributeForm;
